import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, aboutInfo } from '@/mocks/comics';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.name}`,
  description: `${siteInfo.name}是国内领先的免费漫画阅读平台，提供海量国产漫画、日本漫画、韩国漫画在线免费阅读，支持下拉式浏览。`,
  url: `https://${siteInfo.domain}/about`,
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.name,
    url: `https://${siteInfo.domain}`,
  },
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
    description: siteInfo.description,
    foundingDate: siteInfo.established,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  },
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: '首页', item: `https://${siteInfo.domain}/` },
    { '@type': 'ListItem', position: 2, name: '关于我们', item: `https://${siteInfo.domain}/about` },
  ],
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      if (honeypotField) formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9pdk58senbniolestcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

            <section className="mt-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                关于<span className="text-primary-500">土豪漫画</span>
              </h1>
              <p className="text-sm text-foreground-600 mt-3 max-w-3xl">
                {aboutInfo.brandIntro}
              </p>
            </section>

            <section className="mt-14">
              <div className="bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl p-8 md:p-12 text-white text-center">
                <i className="ri-double-quotes-l text-4xl opacity-30 mb-4 block"></i>
                <p className="text-lg md:text-xl font-medium leading-relaxed max-w-2xl mx-auto">
                  {aboutInfo.mission}
                </p>
              </div>
            </section>

            <section className="mt-14">
              <h2 className="text-2xl font-bold text-foreground-950 mb-8 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                平台优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {aboutInfo.features.map((feat, idx) => (
                  <div key={idx} className="bg-background-100 rounded-xl p-6 hover:bg-background-200/70 transition-colors">
                    <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                      <i className={`${feat.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-950 mb-2 whitespace-nowrap">{feat.title}</h3>
                    <p className="text-sm text-foreground-600">{feat.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="contact" className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl font-bold text-foreground-950 mb-6 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                  联系我们
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-qq-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">QQ群</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.qq}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">公司地址</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-foreground-950 mb-6 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                  在线留言
                </h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-100 rounded-xl p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1 whitespace-nowrap">
                      您的称呼 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
                      placeholder="请输入您的称呼"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1 whitespace-nowrap">
                      电子邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1 whitespace-nowrap">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 transition-all resize-none"
                      placeholder="请输入您的留言内容，最多500字"
                    />
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i> 提交中...
                      </span>
                    ) : '提交留言'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center bg-green-50 rounded-lg py-2 px-3">
                      <i className="ri-check-line mr-1"></i>感谢您的留言，我们会尽快回复！
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-600 text-center bg-red-50 rounded-lg py-2 px-3">
                      <i className="ri-error-warning-line mr-1"></i>{formError}
                    </p>
                  )}
                </form>
              </div>
            </section>

            <section className="mt-14 mb-8 text-center">
              <h2 className="text-2xl font-bold text-foreground-950 mb-4 whitespace-nowrap" style={{ fontFamily: 'var(--font-heading)' }}>
                加入我们
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto mb-6">
                土豪漫画网始终欢迎热爱漫画的你加入，一起打造更好的漫画阅读社区。如有合作意向，请通过上方联系方式与我们沟通。
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 px-6 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i> 返回首页继续阅读
              </Link>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}